import { createContext, useContext, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSyncExternalStore } from "react";

// Toast store outside React component tree - prevents re-renders
class ToastStore {
  constructor() {
    this.toasts = [];
    this.listeners = new Set();
  }

  subscribe = (listener) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = () => {
    return this.toasts;
  };

  addToast = (toast) => {
    this.toasts = [...this.toasts, toast];
    this.emitChange();
  };

  removeToast = (id) => {
    this.toasts = this.toasts.filter((toast) => toast.id !== id);
    this.emitChange();
  };

  emitChange = () => {
    this.listeners.forEach((listener) => listener());
  };
}

const toastStore = new ToastStore();

// Export toastStore for direct access (used by dev-only validation toast helper)
export { toastStore };

const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within ToastProvider");
  }
  return context;
};

export const ToastProvider = ({ children }) => {
  const { t } = useTranslation();

  const valueRef = useRef();

  if (!valueRef.current) {
    valueRef.current = {
      showToast: (message, options = {}) => {
        const id = Date.now() + Math.random();
        const type = options.type || "info";
        // Error toasts don't auto-dismiss, warning toasts default to 5000ms, info toasts default to 5000ms
        const duration =
          type === "error"
            ? null
            : type === "warning"
              ? options.duration ?? 5000
              : options.duration ?? 5000;

        // Extract translation variables from options
        const variables = options.variables || {};

        // Debug: log variables to see what's being passed
        if (Object.keys(variables).length > 0) {
          console.log("Toast variables:", variables);
        }

        // Translate title and description if they're translation keys, passing variables
        // i18next expects variables in the format: t(key, { variable1: value1, ... })
        const translatedTitle = options.title
          ? t(options.title, variables)
          : t(message, variables);
        const translatedDescription = options.description
          ? t(options.description, variables)
          : undefined;

        const newToast = {
          id,
          message: t(message, variables),
          title: translatedTitle,
          description: translatedDescription,
          duration,
          position: options.position || "top",
          type,
        };

        toastStore.addToast(newToast);

        return id;
      },
      hideToast: (id) => {
        toastStore.removeToast(id);
      },
    };
  }

  return (
    <ToastContext.Provider value={valueRef.current}>
      {children}
    </ToastContext.Provider>
  );
};

// Hook to subscribe to toast changes - only ToastContainer uses this
export const useToasts = () => {
  return useSyncExternalStore(
    toastStore.subscribe,
    toastStore.getSnapshot,
    toastStore.getSnapshot
  );
};
